import { FC, useCallback, useEffect, type JSX } from 'react';

import { useSelector } from '@/api/state';
import { getUploadsByIds } from '@/utils/stateAccessors';
import FileUploadPersistRemoveControl from '@payaca/components/fileUploadPersistRemoveControl/FileUploadPersistRemoveControl';
import * as uploadsActions from '@payaca/store/uploads/uploadsActions';
import { UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP } from '@payaca/types/uploadTypes';
import { TIconName } from '@payaca/untitled-icons';
import { useDispatch } from 'react-redux';

type Props = {
  label?: string;
  description?: string;
  uploadIds: number[];
  onUploadCollectionChange: (uploadIds: number[]) => void;
  isDisabled?: boolean;
  acceptFileTypes?: string[];
  allowMultipleUploads?: boolean;
  fileSizeLimitBytes?: number;
  uploadTriggerIcon?: TIconName;
  showFileNames?: boolean;
  canRemove?: boolean;
  customPersistUpload?: (file: File) => Promise<any>;
  customRemoveUpload?: (uploadId: number) => Promise<any>;
  canAdd?: boolean;
};

const UploadCollectionControl: FC<Props> = ({
  label,
  description,
  uploadIds,
  onUploadCollectionChange,
  isDisabled = false,
  acceptFileTypes,
  allowMultipleUploads = true,
  fileSizeLimitBytes = UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP,
  uploadTriggerIcon,
  showFileNames = true,
  canRemove,
  canAdd,
  customPersistUpload,
  customRemoveUpload,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const uploads = useSelector((state) =>
    getUploadsByIds(state, uploadIds || [])
  );

  useEffect(() => {
    if (uploadIds.length) {
      dispatch(uploadsActions.getUploads.request({ uploadIds }));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'uploadIds', and 'dispatch'
  }, [uploadIds?.length]);

  const onPersistUploadsSuccess = useCallback(
    (newUploadIds: number[]) => {
      newUploadIds.map((newUploadId) =>
        dispatch(uploadsActions.requestGetUpload(newUploadId))
      );
      onUploadCollectionChange([...uploadIds, ...newUploadIds]);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [onUploadCollectionChange, uploadIds]
  );

  const onPersistUpload = useCallback(
    (file: File) => {
      if (
        file.size >
        Math.min(fileSizeLimitBytes, UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP)
      )
        return new Promise<any>((resolve, reject) => {
          reject(new Error());
        });

      if (customPersistUpload) {
        return customPersistUpload(file);
      }
      return new Promise<any>((resolve, reject) => {
        dispatch(
          uploadsActions.requestPersistUpload(
            { file: file },
            (uploadId: number) => {
              resolve(uploadId);
            }
          )
        );
      });
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'fileSizeLimitBytes'
    [customPersistUpload]
  );

  const onRemovePersistedUpload = useCallback(
    (uploadId: number) => {
      if (customRemoveUpload) {
        return customRemoveUpload(uploadId);
      }
      return new Promise<void>((resolve) => {
        onUploadCollectionChange(uploadIds.filter((x) => x != uploadId));
        resolve();
      });
    },
    [customRemoveUpload, onUploadCollectionChange, uploadIds]
  );

  return (
    <div className="upload-collection-control">
      <FileUploadPersistRemoveControl
        persistedFiles={uploads.map((x) => {
          return {
            fileName: x.fileName,
            identifier: x.id,
            mimeType: x.mimeType,
            thumbnailUrl: x.thumbnailUrl,
            downloadUrl: x.url,
          };
        })}
        persistFile={onPersistUpload}
        removePersistedFile={onRemovePersistedUpload}
        onPersistFilesSuccess={onPersistUploadsSuccess}
        canRemove={canRemove}
        canAdd={canAdd}
        allowFileNameModification={false}
        allowMultipleUploads={allowMultipleUploads}
        acceptFileTypes={acceptFileTypes}
        isDisabled={isDisabled}
        label={label}
        description={description}
        uploadTriggerIcon={uploadTriggerIcon}
        showFileNames={showFileNames}
        enableDragAndDrop={true}
      />
    </div>
  );
};

export default UploadCollectionControl;
